import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import {
  Plane, Navigation, Route as RouteIcon, ClipboardList, LayoutDashboard,
  LogOut, Compass, UserRound, Globe, BookMarked,
} from "lucide-react";

const NAV = [
  { to: "/", label: "Tableau de bord", icon: LayoutDashboard },
  { to: "/pilots", label: "Pilotes", icon: UserRound },
  { to: "/aircraft", label: "Avions", icon: Plane },
  { to: "/resources", label: "Ressources", icon: Globe },
  { to: "/vac", label: "Cartes VAC", icon: BookMarked },
  { to: "/routes", label: "Routes", icon: RouteIcon },
  { to: "/briefings", label: "Briefings", icon: ClipboardList },
];

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  const doLogout = async () => {
    await logout();
    navigate("/login");
  };

  const isActive = (to) =>
    to === "/" ? location.pathname === "/" : location.pathname.startsWith(to);

  return (
    <div className="grain relative min-h-screen bg-[#070C18]">
      <header className="sticky top-0 z-50 border-b border-slate-800/80 bg-[#070C18]/85 backdrop-blur-md">
        <div className="mx-auto flex max-w-[1500px] items-center justify-between px-4 py-3 sm:px-8">
          <div className="flex items-center gap-8">
            <Link to="/" data-testid="brand-home-link" className="flex items-center gap-2.5">
              <div className="flex h-9 w-9 items-center justify-center rounded-lg border border-cyan-500/30 bg-cyan-500/10">
                <Navigation className="h-5 w-5 text-cyan-400" strokeWidth={2} />
              </div>
              <div className="leading-tight">
                <div className="font-display text-sm font-bold tracking-wide text-slate-50">VFR NAV</div>
                <div className="flex items-center gap-1 text-[10px] uppercase tracking-widest text-cyan-500/70">
                  <Compass className="h-2.5 w-2.5" /> EFB Console
                </div>
              </div>
            </Link>
            <nav className="hidden items-center gap-1 md:flex">
              {NAV.map((n) => {
                const Icon = n.icon;
                const active = isActive(n.to);
                return (
                  <Link key={n.to} to={n.to} data-testid={`nav-${n.to === "/" ? "dashboard" : n.to.slice(1)}`}
                    className={`flex items-center gap-2 rounded-lg px-3 py-2 text-sm font-medium transition-all ${
                      active
                        ? "bg-cyan-500/15 text-cyan-300 shadow-[0_0_12px_rgba(0,229,255,0.12)]"
                        : "text-slate-400 hover:bg-slate-800/60 hover:text-slate-200"
                    }`}>
                    <Icon className="h-4 w-4" /> {n.label}
                  </Link>
                );
              })}
            </nav>
          </div>
          <div className="flex items-center gap-3">
            <div className="hidden text-right sm:block">
              <div className="text-xs font-medium text-slate-200">{user?.name}</div>
              <div className="text-[10px] uppercase tracking-wider text-slate-500">{user?.role}</div>
            </div>
            <Button data-testid="logout-button" onClick={doLogout} variant="ghost" size="sm"
              className="text-slate-400 hover:bg-rose-500/10 hover:text-rose-300">
              <LogOut className="h-4 w-4" />
            </Button>
          </div>
        </div>
        {/* mobile nav */}
        <nav className="flex items-center gap-1 overflow-x-auto border-t border-slate-800/60 px-2 py-2 md:hidden">
          {NAV.map((n) => {
            const Icon = n.icon;
            const active = isActive(n.to);
            return (
              <Link key={n.to} to={n.to}
                className={`flex shrink-0 items-center gap-1.5 rounded-lg px-3 py-1.5 text-xs font-medium ${
                  active ? "bg-cyan-500/15 text-cyan-300" : "text-slate-400"
                }`}>
                <Icon className="h-3.5 w-3.5" /> {n.label}
              </Link>
            );
          })}
        </nav>
      </header>
      <main className="relative z-10 mx-auto max-w-[1500px] px-4 py-6 sm:px-8 sm:py-8">{children}</main>
    </div>
  );
}
